/*
Retry policy for offline-aware React Query queries, extracted as pure functions
so the "don't retry while offline" behavior is unit-testable.

Before this, these queries used React Query's default 3× exponential backoff
(~1+2+4s ≈ 7s) even when the browser already knew it was offline. On a cold
offline launch that delayed the cached-identity / cached-aggregate fallback by
~7s of pointless retries (getCurrentUser was the dominant cost). When offline we
fail fast and fall back to cache immediately; when online we keep a normal,
bounded set of retries for genuine transient blips. A server-down-but-online
case still reports `navigator.onLine === true`, so it retries as before.
*/

export const MAX_USER_QUERY_RETRIES = 3;
export const MAX_ARTIFACT_QUERY_RETRIES = 2;

/**
 * getCurrentUser retry decision: never retry an auth failure (it won't change),
 * never retry while offline (fail fast → cached-identity fallback), otherwise
 * retry transient/server errors up to the cap.
 */
export function shouldRetryUserQuery(args: {
  failureCount: number;
  isUnauthorized: boolean;
  isOnline: boolean;
  maxRetries?: number;
}): boolean {
  const {
    failureCount,
    isUnauthorized,
    isOnline,
    maxRetries = MAX_USER_QUERY_RETRIES,
  } = args;
  if (isUnauthorized) return false;
  if (!isOnline) return false;
  return failureCount < maxRetries;
}

/**
 * Offline-cached (artifact) query retry decision: never retry while offline
 * (the hydrated cache is the fallback), otherwise a small bounded number of
 * retries for transient blips.
 */
export function shouldRetryArtifactQuery(args: {
  failureCount: number;
  isOnline: boolean;
  maxRetries?: number;
}): boolean {
  const {
    failureCount,
    isOnline,
    maxRetries = MAX_ARTIFACT_QUERY_RETRIES,
  } = args;
  if (!isOnline) return false;
  return failureCount < maxRetries;
}
